import React, { useEffect, useRef } from 'react';
import type { Itinerary } from '../types/api';

declare const maplibregl: any;

interface RouteMapProps {
  origin: { lat: number; lon: number } | null;
  destination: { lat: number; lon: number } | null;
  selectedItinerary: Itinerary | null;
  setOrigin: (loc: { lat: number; lon: number }) => void;
  setDestination: (loc: { lat: number; lon: number }) => void;
}

function decodePolyline(encoded: string): [number, number][] {
  const points: [number, number][] = [];
  let index = 0;
  let lat = 0;
  let lon = 0;
  while (index < encoded.length) {
    let b: number;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dlat;
    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlon = result & 1 ? ~(result >> 1) : result >> 1;
    lon += dlon;
    points.push([lat / 1e5, lon / 1e5]);
  }
  return points;
}

function addMarker(map: any, loc: { lat: number; lon: number }, className: string, text: string) {
  const el = document.createElement('div');
  el.className = className;
  el.textContent = text;
  return new maplibregl.Marker({ element: el }).setLngLat([loc.lon, loc.lat]).addTo(map);
}

function getMidpoint(coords: [number, number][]): [number, number] {
  const mid = Math.floor(coords.length / 2);
  return coords[mid];
}

const EMPTY_GEOJSON: any = { type: 'FeatureCollection', features: [] };

const LINE_COLORS: Record<string, string> = {
  'blue line': '#2563eb',
  'green line': '#16a34a',
  'red line': '#dc2626',
  'orange line': '#ea580c',
  'gold line': '#eab308',
};

const METRO_LETTER_RE = /^METRO\s+([A-E])\s+Line$/i;

const BUS_COLORS = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#06b6d4',
  '#84cc16', '#f43f5e', '#6366f1', '#14b8a6', '#a855f7',
  '#0ea5e9', '#d946ef', '#22d3ee', '#fb923c', '#34d399',
];

function getRouteLabel(leg: { mode: string; route?: { shortName?: string; longName?: string } | null }): string {
  const sn = leg.route?.shortName || '';
  const ln = leg.route?.longName || '';

  // Metro lettered lines: "METRO D Line" -> "D"
  const m = ln.match(METRO_LETTER_RE);
  if (m) return m[1].toUpperCase();

  const name = (sn || ln).toLowerCase();
  for (const key of Object.keys(LINE_COLORS)) {
    if (name.includes(key)) {
      const lineName = key.replace(' line', '');
      return lineName.charAt(0).toUpperCase() + lineName.slice(1);
    }
  }
  // Blue Line Bus
  if (ln.toLowerCase().includes('blue line bus')) return 'BL Bus';
  return sn || leg.mode;
}

function isMetroLetterLine(leg: { route?: { longName?: string } | null }): boolean {
  return METRO_LETTER_RE.test(leg.route?.longName || '');
}

function getTransitColor(leg: { mode: string; route?: { shortName?: string; longName?: string } | null }): string {
  const sn = leg.route?.shortName || '';
  const ln = leg.route?.longName || '';
  const name = (sn || ln).toLowerCase();

  for (const [key, color] of Object.entries(LINE_COLORS)) {
    if (name.includes(key)) return color;
  }
  // Metro lettered lines (A, B, C, D, E) get gray
  if (isMetroLetterLine(leg)) return '#6b7280';
  if (leg.mode === 'RAIL' || leg.mode === 'TRAM' || leg.mode === 'SUBWAY') return '#16a34a';
  if (leg.mode === 'FERRY') return '#a855f7';
  const routeName = sn || ln;
  let hash = 0;
  for (let i = 0; i < routeName.length; i++) {
    hash = ((hash << 5) - hash + routeName.charCodeAt(i)) | 0;
  }
  return BUS_COLORS[Math.abs(hash) % BUS_COLORS.length];
}

function addRouteLabel(map: any, lngLatCoords: [number, number][], label: string, color: string) {
  const midIdx = Math.floor(lngLatCoords.length / 2);
  const mid = lngLatCoords[midIdx]; // [lon, lat]
  const el = document.createElement('div');
  el.textContent = label;
  el.style.cssText = `
    background: ${color};
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    white-space: nowrap;
    text-shadow: 0 1px 2px rgba(0,0,0,0.5);
    box-shadow: 0 1px 4px rgba(0,0,0,0.4);
    pointer-events: none;
    line-height: 1.2;
  `;
  const marker = new maplibregl.Marker({ element: el })
    .setLngLat(mid)
    .addTo(map);
  return marker;
}

export default function RouteMap({ origin, destination, selectedItinerary, setOrigin, setDestination }: RouteMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const labelMarkersRef = useRef<any[]>([]);
  const drawnKey = useRef<string>('');
  const dynamicLayersRef = useRef<string[]>([]);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '&copy; OpenStreetMap contributors',
          },
  'bike-src': { type: 'geojson', data: EMPTY_GEOJSON },
  'walk-src': { type: 'geojson', data: EMPTY_GEOJSON },
        },
        layers: [
          { id: 'osm', type: 'raster', source: 'osm' },
          { id: 'walk-casing', type: 'line', source: 'walk-src', paint: { 'line-color': '#222', 'line-width': 10, 'line-opacity': 0.3 }, layout: { 'line-cap': 'round', 'line-join': 'round' } },
          { id: 'walk-line', type: 'line', source: 'walk-src', paint: { 'line-color': '#9ca3af', 'line-width': 4, 'line-opacity': 0.8 }, layout: { 'line-cap': 'round', 'line-join': 'round' } },
          { id: 'bike-connector', type: 'line', source: 'bike-src', paint: { 'line-color': '#22c55e', 'line-width': 3, 'line-opacity': 0.5, 'line-dasharray': [4, 3] }, layout: { 'line-cap': 'round', 'line-join': 'round' }, filter: ['==', ['get', 'connector'], true] },
          { id: 'bike-casing', type: 'line', source: 'bike-src', paint: { 'line-color': '#222', 'line-width': 10, 'line-opacity': 0.4 }, layout: { 'line-cap': 'round', 'line-join': 'round' }, filter: ['!=', ['get', 'connector'], true] },
          { id: 'bike-line', type: 'line', source: 'bike-src', paint: { 'line-color': '#22c55e', 'line-width': 6, 'line-opacity': 0.9 }, layout: { 'line-cap': 'round', 'line-join': 'round' }, filter: ['!=', ['get', 'connector'], true] },
        ],
      },
      center: [-93.27, 44.98],
      zoom: 12,
    });

    map.addControl(new maplibregl.NavigationControl());

    let clickCount = 0;
    map.on('click', (e: any) => {
      const loc = { lat: e.lngLat.lat, lon: e.lngLat.lng };
      clickCount++;
      if (clickCount === 1) {
        setOrigin(loc);
      } else {
        setDestination(loc);
        clickCount = 0;
      }
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m: any) => m.remove());
    markersRef.current = [];

    if (origin) {
      markersRef.current.push(addMarker(map, origin, 'map-marker origin-marker', 'A'));
    }
    if (destination) {
      markersRef.current.push(addMarker(map, destination, 'map-marker dest-marker', 'B'));
    }
  }, [origin, destination]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Remove previous dynamic transit layers/sources
    for (const layerId of dynamicLayersRef.current) {
      try { map.removeLayer(layerId); } catch (_) {}
    }
    for (const layerId of dynamicLayersRef.current) {
      const srcId = layerId.replace(/-casing$|-line$/, '');
      try { map.removeSource(srcId); } catch (_) {}
    }
    dynamicLayersRef.current = [];

    // Remove previous label markers
    labelMarkersRef.current.forEach((m: any) => m.remove());
    labelMarkersRef.current = [];

    if (!selectedItinerary) {
      try {
        map.getSource('bike-src')?.setData(EMPTY_GEOJSON);
        map.getSource('walk-src')?.setData(EMPTY_GEOJSON);
      } catch (_) {}
      drawnKey.current = '';
      return;
    }

    const key = selectedItinerary.startTime + '|' + selectedItinerary.legs.map(l => l.mode + l.from.name + l.to.name).join('|');
    if (key === drawnKey.current) return;
    drawnKey.current = key;

    const bikeFeatures: any[] = [];
    const walkFeatures: any[] = [];
    const transitByRoute: Record<string, { features: any[]; label: string; color: string }> = {};

    let transitIdx = 0;
    let legIdx = 0;
    for (let li = 0; li < selectedItinerary.legs.length; li++) {
      const leg = selectedItinerary.legs[li];

      if (!leg.transitLeg) {
        // Merged bike legs are already rendered as part of the first leg in the group
        if (leg.mergedBikeLeg) {
          legIdx++;
          continue;
        }
        // For bike legs: prefer bike-planner geometry (road-following) over OTP polyline
        // For non-bike non-transit legs (walk): use OTP polyline as before
        if (leg.mode === 'BICYCLE' && leg.bikeGeometry && leg.bikeGeometry.length >= 2) {
          const lngLatCoords = leg.bikeGeometry;

          // Transit-to-bike gap: connect transit end to bike start
          const prevLeg = li > 0 ? selectedItinerary.legs[li - 1] : null;
          if (prevLeg && prevLeg.transitLeg) {
            const transitEnd: [number, number] = [prevLeg.to.lon, prevLeg.to.lat];
            const firstPt = lngLatCoords[0];
            const gapM = Math.abs(transitEnd[0] - firstPt[0]) * 111000 +
                         Math.abs(transitEnd[1] - firstPt[1]) * 111000;
            if (gapM > 50 && gapM < 5000) {
              bikeFeatures.push({
                type: 'Feature',
                properties: { legIdx },
                geometry: { type: 'LineString', coordinates: [transitEnd, firstPt] },
              });
            }
          }

          // Bike-to-transit gap: connect bike end to transit start
          const nextLeg = selectedItinerary.legs[li + 1];
          if (nextLeg && nextLeg.transitLeg) {
            const transitStart: [number, number] = [nextLeg.from.lon, nextLeg.from.lat];
            const lastPt = lngLatCoords[lngLatCoords.length - 1];
            const gapM = Math.abs(transitStart[0] - lastPt[0]) * 111000 +
                         Math.abs(transitStart[1] - lastPt[1]) * 111000;
            if (gapM > 50 && gapM < 5000) {
              bikeFeatures.push({
                type: 'Feature',
                properties: { legIdx },
                geometry: { type: 'LineString', coordinates: [lastPt, transitStart] },
              });
            }
          }

          bikeFeatures.push({
            type: 'Feature',
            properties: { legIdx },
            geometry: { type: 'LineString', coordinates: lngLatCoords },
          });
          legIdx++;
          continue;
        }

        const geomStr = leg.legGeometry?.points;
        if (!geomStr) continue;

        const decoded = decodePolyline(geomStr);
        if (decoded.length < 2) continue;

        const lngLatCoords = decoded.map((pt): [number, number] => [pt[1], pt[0]]);

        if (leg.mode === 'BICYCLE') {
          // OTP polyline fallback for bike legs without bike-planner geometry
          const feature = {
            type: 'Feature',
            properties: { legIdx },
            geometry: { type: 'LineString', coordinates: lngLatCoords },
          };
          bikeFeatures.push(feature);
        } else {
          const feature = {
            type: 'Feature',
            properties: { legIdx },
            geometry: { type: 'LineString', coordinates: lngLatCoords },
          };
          walkFeatures.push(feature);
        }
      } else {
        const geomStr = leg.legGeometry?.points;
        if (!geomStr) { legIdx++; continue; }

        const decoded = decodePolyline(geomStr);
        if (decoded.length < 2) { legIdx++; continue; }

        const transitCoords = decoded.map((pt): [number, number] => [pt[1], pt[0]]);
        const feature = {
          type: 'Feature',
          properties: { legIdx },
          geometry: { type: 'LineString', coordinates: transitCoords },
        };

        const routeId = leg.route?.gtfsId || `route-${transitIdx}`;
        if (!transitByRoute[routeId]) {
          transitByRoute[routeId] = {
            features: [],
            label: getRouteLabel(leg),
            color: getTransitColor(leg),
          };
        }
        transitByRoute[routeId].features.push(feature);
        transitIdx++;
      }
      legIdx++;
    }

    // Add dynamic transit sources/layers per route + label markers
    for (const [routeId, routeData] of Object.entries(transitByRoute)) {
      const srcId = `transit-${routeId}`;
      const casingId = `transit-${routeId}-casing`;
      const lineId = `transit-${routeId}-line`;

      try {
        map.addSource(srcId, { type: 'geojson', data: { type: 'FeatureCollection', features: routeData.features } });
        map.addLayer({
          id: casingId, type: 'line', source: srcId,
          paint: { 'line-color': '#222', 'line-width': 12, 'line-opacity': 0.4 },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        });
        map.addLayer({
          id: lineId, type: 'line', source: srcId,
          paint: { 'line-color': routeData.color, 'line-width': 8, 'line-opacity': 0.9 },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        });
        dynamicLayersRef.current.push(casingId, lineId);

        // Add label marker at midpoint
        if (routeData.features.length > 0) {
          const firstCoords = routeData.features[0].geometry.coordinates;
          const labelMarker = addRouteLabel(map, firstCoords, routeData.label, routeData.color);
          labelMarkersRef.current.push(labelMarker);
        }
      } catch (_) {}
    }

    try {
      const bikeGeoJSON = bikeFeatures.length > 0
        ? { type: 'FeatureCollection', features: bikeFeatures }
        : EMPTY_GEOJSON;
      const walkGeoJSON = walkFeatures.length > 0
        ? { type: 'FeatureCollection', features: walkFeatures }
        : EMPTY_GEOJSON;
      map.getSource('bike-src').setData(bikeGeoJSON);
      map.getSource('walk-src').setData(walkGeoJSON);
    } catch (_) {}

    // Fit bounds
    const allCoords: [number, number][] = [];
    for (const feat of [...bikeFeatures, ...walkFeatures]) {
      const coords = feat.geometry.coordinates;
      for (const pt of coords) allCoords.push(pt);
    }
    for (const routeData of Object.values(transitByRoute)) {
      for (const feat of routeData.features) {
        const coords = feat.geometry.coordinates;
        for (const pt of coords) allCoords.push(pt);
      }
    }
    if (allCoords.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      allCoords.forEach((pt: any) => bounds.extend(pt));
      map.fitBounds(bounds, { padding: 60, maxZoom: 15 });
    }
  }, [selectedItinerary]);

  return <div ref={mapContainer} className="route-map" />;
}
